import React from "react";
import { useApp } from "../contexts/AppContext";
import { useGentleReminders } from "../lib/useGentleReminders";
import { Moon, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

/**
 * Soft, dismissible reminder banner. Appears at most once per day, only when:
 *  - parent has reminders_enabled = true
 *  - current time is within 30 min of reminder_time
 *  - today is NOT a quiet day
 *
 * Tone is gentle and non-pressuring. There is no scoring, no streak, no urgency.
 */
export const GentleReminderBanner = () => {
  const { settings, parent } = useApp();
  const { activeMessage, dismiss } = useGentleReminders(settings);
  const location = useLocation();

  if (!parent || !activeMessage) return null;
  // Don't overlay onto bedtime/hive-reset pages — already in the calm space
  if (location.pathname.includes("/bedtime") || location.pathname.includes("/hive-reset")) return null;

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md print:hidden"
      data-testid="gentle-reminder-banner"
      role="status"
    >
      <div
        className="rounded-3xl p-5 shadow-xl flex items-start gap-3"
        style={{ background: "var(--bn-surface-alt)", border: "1px solid var(--bn-border)" }}
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 bn-breathe"
          style={{ background: "var(--bn-primary)" }}
        >
          <Moon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="display text-base font-semibold mb-1 leading-relaxed">
            {activeMessage}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={dismiss}
              className="text-sm font-semibold underline-offset-4 hover:underline"
              style={{ color: "var(--bn-text-secondary)" }}
              data-testid="reminder-later"
            >
              Maybe later
            </button>
            <span className="text-sm" style={{ color: "var(--bn-text-secondary)" }}>·</span>
            <Link
              to="/profiles"
              onClick={dismiss}
              className="text-sm font-semibold underline-offset-4 hover:underline"
              style={{ color: "var(--bn-accent-terracotta)" }}
              data-testid="reminder-go-bedtime"
            >
              Begin a gentle reset
            </Link>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-full hover:bg-[var(--bn-surface)] flex-shrink-0"
          aria-label="Dismiss"
          data-testid="reminder-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default GentleReminderBanner;
